import React, { useState } from 'react';
import { useLoanEligibility } from '../hooks/useLoanEligibility.ts';
import { CheckCircle, AlertCircle, Info, Calculator } from 'lucide-react';
import { PERMISSIONS } from '../utils/permissions';

const LoanTypeCard = ({ loanType, isSelected, onSelect }) => {
  return (
    <div 
      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      <h3 className="font-semibold text-lg">{loanType.loan_type}</h3>
      <p className="text-gray-600 text-sm mt-2">
        {loanType.eligibility_breakdown?.type === 'savings_multiplier' 
          ? `Up to ${loanType.eligibility_breakdown?.multiplier}x your savings`
          : `Up to ${loanType.eligible_amount?.toLocaleString()}`
        }
      </p>
      {loanType.requires_guarantors && (
        <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded mt-2">
          Requires Guarantors
        </span>
      )}
    </div>
  );
};



export default function LoanEligibilityPage() {
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const { data: loanTypes, isLoading, error } = useLoanEligibility();

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-2" />
            <span className="text-red-800">Error loading loan eligibility information</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center mb-6">
          <Calculator className="text-blue-600 mr-3" size={24} />
          <h1 className="text-2xl font-bold">Check Loan Eligibility</h1>
        </div>
      
      {/* Loan Type Selection */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Select Loan Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loanTypes?.map((loanType) => (
            <LoanTypeCard
              key={loanType.id}
              loanType={loanType}
              isSelected={selectedLoanType?.id === loanType.id}
              onSelect={() => setSelectedLoanType(loanType)}
            />
          ))}
        </div>
      </div>



      {/* Selected Loan Type Display */}
      {selectedLoanType && (
        <div className="bg-white border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">
            Eligibility for {selectedLoanType.loan_type}
          </h2>

          {/* Member Savings */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Your Current Savings</h3>
            <div className="text-2xl font-bold text-green-600">
              {selectedLoanType.member_savings?.toLocaleString() || 'N/A'}
            </div>
          </div>

          {/* Eligibility Breakdown */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Eligibility Calculation</h3>
            <div className="bg-gray-50 p-4 rounded">
              <div className="text-lg font-semibold">
                {selectedLoanType.eligibility_breakdown?.calculation || 'Calculation not available'}
              </div>
              <div className="text-2xl font-bold text-blue-600 mt-2">
                Maximum Loan: {selectedLoanType.eligible_amount?.toLocaleString() || 'N/A'}
              </div>
            </div>
          </div>

          {/* Guarantor Information */}
          {selectedLoanType.requires_guarantors && (
            <div className="border-t pt-6">
              <h3 className="font-medium text-gray-700 mb-2">Guarantor Requirements</h3>
              <div className="bg-orange-50 border border-orange-200 p-4 rounded">
                <div className="text-orange-800">
                  <p className="mb-2">{selectedLoanType.guarantor_details?.explanation || 'Guarantors are required for this loan type.'}</p>
                  <p className="text-sm">
                    You'll need to select guarantors who will guarantee the amount exceeding this threshold.
                  </p>
                </div>
              </div>
            </div>
          )}


        </div>
      )}

      {/* No Selection State */}
      {!selectedLoanType && (
        <div className="bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg p-8 text-center">
          <Info className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Loan Type</h3>
          <p className="text-gray-500">
            Choose a loan type above to see your eligibility details and requirements.
          </p>
        </div>
      )}
    </div>
    
  );
} 